"use client";

import JobReview from "../jobReviews";

import { useMutation, useQueryClient } from "react-query";
import { CheckIcon, Clock11Icon, LocateIcon } from "lucide-react";
import { formatCurrency, formatDate, nairaSymbol } from "utills/helper";
import useJob from "hooks/useJob";
import { useSearchParams } from "react-router-dom";
import Modal from "Common/Components/Modal";
import { FormEvent, useState } from "react";
import { updateJobStatus } from "services/job.service";

export default function JobDetails() {
  const searchParams = useSearchParams()[0];
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("");
  // const queryClient = useQueryClient();
  // const navigation = useNavigate();

  const jobId = searchParams.get("jobId");

  const { isLoading, data, refetch } = useJob(jobId!);

  const { isLoading: verifying, mutate } = useMutation(
    async (input: { status: string; comment: string }) =>
      await updateJobStatus(jobId!, input),
    {
      onSuccess(data) {
        refetch();
        setShowComment(false);
        alert(data.message);
      },
      onError(err: any) {
        alert(err?.response?.data?.message);
      },
    }
  );

  const toggleComment = () => {
    setShowComment(!showComment);
  };

  const handleStatus = (status: string) => {
    setStatus(status);
    toggleComment();
  };

  const updateStatus = (e: FormEvent) => {
    e.preventDefault();
    mutate({ status, comment });
  };
  return (
    <div className=" flex flex-col gap-8">
      {isLoading ? (
        <div className=" flex justify-center items-center"></div>
      ) : (
        <>
          <div>
            <div className=" w-full relative h-[250px] rounded-t-md overflow-hidden">
              <img
                src={data?.data?.coverPhoto?.url!}
                alt=""
                className=" object-cover w-full h-full absolute"
              />
            </div>

            <div className=" bg-[#ffffff] rounded-sm shadow-md flex flex-col p-3">
              <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
                {data?.data?.title}
              </span>
              <div className="flex items-center gap-4 mt-4">
                <span className="text-gray-400 text-sm font-body font-medium leading-4 ">
                  {data?.data?.employmentType}
                </span>
                <span className=" text-gray-400">|</span>
                <span className="text-gray-400 flex items-center text-sm font-body font-medium leading-4 ">
                  <LocateIcon /> {data?.data?.location}, {data?.data?.area}
                </span>
              </div>

              <div className=" flex items-center mt-4 gap-2">
                <span className="text-gray-400  flex items-center text-sm font-body font-medium leading-4 ">
                  <Clock11Icon />
                </span>
                <span className="text-gray-400  flex items-center text-sm font-body font-medium leading-4 ">
                  Posted {new Date(data?.data?.createdAt!).toDateString()}
                </span>
              </div>
              <div className=" flex items-center mt-4 gap-2">
                <span className="text-gray-400  flex items-center text-sm font-body font-medium leading-4 ">
                  <CheckIcon />
                </span>
                <span
                  className={` ${
                    data?.data?.status === "approved"
                      ? "text-blue-500"
                      : data?.data?.status === "suspended"
                      ? "text-yellow-500"
                      : "text-red-500"
                  }  flex items-center text-sm font-body font-medium leading-4 `}
                >
                  Status: {data?.data?.status}
                </span>
              </div>

              <div className=" flex flex-col lg:flex-row gap-2 lg:items-center justify-between mt-8 lg:mt-0 ">
                <span className="text-lg text-body font-heading font-semibold leading-5 mt-8">
                  {nairaSymbol}
                  {formatCurrency(data?.data?.minSalary!)} - {nairaSymbol}
                  {formatCurrency(data?.data?.maxSalary!)}
                </span>

                <div className=" flex items-center gap-8 lg:mt-6">
                  <button
                    onClick={() => handleStatus("approved")}
                    className=" p-2 bg-blue-500 text-white text-xs rounded-md hover:opacity-65"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleStatus("declined")}
                    className=" p-2 bg-red-500 text-white text-xs rounded-md hover:opacity-65"
                  >
                    Decline
                  </button>
                  <button
                    onClick={() => handleStatus("suspended")}
                    className=" p-2 bg-yellow-500 text-white text-xs rounded-md hover:opacity-65"
                  >
                    Suspend
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className=" bg-[#ffffff] rounded-sm shadow-md flex flex-col p-3">
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Employer
            </span>
            <span className="text-gray-400 mt-2 flex items-center text-sm font-body font-medium leading-4 ">
              {data?.data?.owner?.firstName} {data?.data?.owner?.lastName}
            </span>
          </div>

          <div className=" bg-[#ffffff] rounded-sm shadow-md flex flex-col p-3">
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Job Description
            </span>
            <span className="text-gray-400 mt-2  flex items-center text-sm font-body font-medium leading-4 ">
              {data?.data?.description}
            </span>
            <div className=" border-b border-muted/30 py-4" />
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Location
            </span>
            <span className="text-gray-400 mt-2  flex items-center text-sm font-body font-medium leading-4 ">
              {data?.data?.location} {data?.data?.area}
            </span>

            <div className=" border-b border-muted/30 py-4" />
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Job nature
            </span>
            <span className="text-gray-400 mt-2 capitalize  flex items-center text-sm font-body font-medium leading-4 ">
              {data?.data?.jobType}
            </span>

            <div className=" border-b border-muted/30 py-4" />
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Required Experience
            </span>
            <span className="text-gray-400 mt-2  flex items-center text-sm font-body font-medium leading-4 ">
              {data?.data?.yoe}
            </span>

            <div className=" border-b border-muted/30 py-4" />
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Expected Start Date
            </span>
            <span className="text-gray-400 mt-2  flex items-center text-sm font-body font-medium leading-4 ">
              {formatDate(data?.data?.expectedStartDate!)}
            </span>
          </div>

          <div className=" bg-[#ffffff] rounded-sm shadow-md flex flex-col p-3">
            <span className=" font-body font-medium leading-5 text-base lg:text-lg mt-4">
              Required Skills
            </span>
            <div className="flex gap-4 flex-wrap mt-2">
              {data?.data.requiredSkills?.map((item: string, i: number) => {
                return (
                  <div
                    className="py-2 px-4 w-fit text-white  relative  flex items-center justify-center text-center text-background bg-green-500 rounded"
                    key={i}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          </div>

          <JobReview job={data?.data!} />
        </>
      )}

      <Modal
        show={showComment}
        onHide={toggleComment}
        id="defaultModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            This job post will get {status}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 overflow-y-auto">
          <form action="#!" onSubmit={updateStatus}>
            <div className="mb-3">
              <label className="inline-block mb-2 text-base font-medium">
                Additional Comment
              </label>
              <textarea
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
                placeholder="Enter notice to job owner"
                name="comment"
                onChange={({ target }) => setComment(target.value)}
                value={comment}
                required
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                data-modal-close="addDocuments"
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                onClick={toggleComment}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {verifying ? "Loading..." : "Submit"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
